import axios from 'axios';
// Get API base URL using environment variables
const getApiBaseUrl = () => {
  // For production builds, use build-time environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For local development
  if (process.env.NODE_ENV === 'development' || 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1') {
    return process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';
  }
  
  // Fallback for production if REACT_APP_API_URL is not set
  return 'https://saleswit-backend.azurewebsites.net';
};

// Determine if we're in development
const isDevelopment = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

const API_BASE_URL = getApiBaseUrl();



// Create axios instance with environment-specific configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '300000'),
  headers: {
    'Content-Type': 'application/json',
  },
  // Environment-specific withCredentials
  withCredentials: false, // Bearer tokens don't need credentials flag
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Debug logging in development
  
  
  return config;
}, (error) => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
  
    return response;
  },
  (error) => {
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      message: error.message,
      baseURL: error.config?.baseURL,
      data: error.response?.data
    });
    
    // Handle 401 errors (unauthorized)
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
    }
    
    return Promise.reject(error);
  }
);

// Make API request with resource quota check

// Authentication functions


export const authenticateWithFirebase = async (firebaseToken) => {
  try {
    const response = await api.post('/api/auth/firebase', { token: firebaseToken });
    
    return response.data;
  } catch (error) {
    console.error("Firebase authentication error:", error);
    throw error.response ? error.response.data : { message: error.message };
  }
};

// CD functions - ONLY CREATE has resource tracking, viewing does not
// ===== ADD THESE NEW RESEARCHER PROFILE FUNCTIONS =====

// Researcher Profile API calls
export const getResearcherProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    const response = await api.get('/api/cd/researcher-profile');
    return response.data;
  } catch (error) {
    console.error("Researcher profile fetch error:", error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Authentication expired. Please log in again.');
    }
    
    throw error.response ? error.response.data : { message: error.message };
  }
};

export const saveResearcherProfile = async (profileData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    const response = await api.post('/api/cd/researcher-profile', profileData);
    return response.data;
  } catch (error) {
    console.error("Researcher profile save error:", error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Authentication expired. Please log in again.');
    }
    
    throw error.response ? error.response.data : { message: error.message };
  }
};

export const checkResearcherProfileExists = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    const response = await api.get('/api/cd/researcher-profile/exists');
    return response.data;
  } catch (error) {
    console.error("Researcher profile check error:", error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Authentication expired. Please log in again.');
    }
    
    throw error.response ? error.response.data : { message: error.message };
  }
};

// ===== UPDATE EXISTING createCD FUNCTION =====
// Replace your existing createCD function with this updated version:

export const createCD = async (formData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    const response = await api.post('/api/cd', formData);
    return response.data;
  } catch (error) {
    console.error("CD creation error:", error);
    
    // ✅ CHECK FOR QUOTA EXCEEDED ERROR
    if (error.response && error.response.data && error.response.data.error_type === 'quota_exceeded') {
      throw {
        isQuotaExceeded: true,
        message: error.response.data.message || 'You have reached your request limit.',
        response: error.response,
        ...error.response.data
      };
    }
    
    // ✅ CHECK FOR PROFILE REQUIRED ERROR
    if (error.response && error.response.data && error.response.data.error_type === 'profile_required') {
      throw {
        isProfileRequired: true,
        message: error.response.data.message || 'Please complete your company profile in Settings.',
        response: error.response,
        ...error.response.data
      };
    }
    
    // If it's a 401 error, the token might be expired
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Authentication expired. Please log in again.');
    }

    // Transport-level cutoff: CloudFront/ALB origin timeout (502/503/504),
    // a client-side axios timeout (ECONNABORTED), or a dropped connection
    // (no response). The request was severed but the backend keeps running and
    // will save the report. Flag it so the caller can poll for the result
    // instead of surfacing a failure to the user.
    const httpStatus = error.response && error.response.status;
    if (error.code === 'ECONNABORTED' || !error.response || httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
      throw {
        isTimeout: true,
        message: 'Report is taking longer than usual and is still generating.'
      };
    }

    throw error.response ? error.response.data : { message: error.message };
  }
};

// VIEWING functions - NO quota checks, users can always view existing reports
export const getCDs = async () => {
  try {
    
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    // NO quota check for viewing existing CDs
    const response = await api.get('/api/cd');
    
    // NO quota decrement for viewing
    return response.data;
  } catch (error) {
    console.error("CD fetch error:", error);
    
    // If it's a 401 error, the token might be expired
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Authentication expired. Please log in again.');
    }
    
    throw error.response ? error.response.data : { message: error.message };
  }
};

export const getCD = async (cdId) => {
  try {
    
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    // NO quota check for viewing existing CD
    const response = await api.get(`/api/cd/${cdId}`);
    
    // NO quota decrement for viewing
    return response.data;
  } catch (error) {
    console.error(`CD fetch error for ID ${cdId}:`, error);
    
    // If it's a 401 error, the token might be expired
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Authentication expired. Please log in again.');
    }
    
    throw error.response ? error.response.data : { message: error.message };
  }
};

// Subscription functions

// Replace this function in api.js:
// Update the apiCall function in api.js for better debugging
export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  // Set default headers, including Authorization if token exists
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  // Add Authorization header if token exists
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    headers: {
      ...defaultHeaders,
      ...options.headers, // User-provided headers override defaults
    },
    ...options,
  };
  
  // Debug logging in development

  
  
  try {
    const response = await fetch(url, config);
    
   
    
    return response;
  } catch (error) {
    console.error('apiCall Network Error:', {
      error: error.message,
      url: url,
      config: config
    });
    throw error;
  }
};
export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/api/auth/update_profile', profileData);
    
    return response.data;
  } catch (error) {
    console.error("Profile update error:", error);
    throw error.response ? error.response.data : { message: error.message };
  }
};

export default api;